import qs from 'qs'

import { emitToast } from './emitToast'
import { ExportDataParams } from '@api/orval/apiHooks'
import { Any, orvalFetch } from '@api/orvalFetcher'
import { dayjs, getAppTimezone } from '@lib/dayjs'

/**
 * For every key that ends with `[]`:
 *   – remove the suffix
 *   – wrap the value into an array if it isn't one already
 * Recurses into nested objects so `search_filter` is handled as well.
 *
 * Example:
 * {
 *   "payout_types[]": "affiliate"
 * }
 * will be converted to:
 * {
 *   "payout_types": ["affiliate"]
 * }
 */
function prepareParamsForQSLibrary(obj: Record<string, unknown>): Record<string, unknown> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const hadArraySuffix = key.endsWith('[]')
      const cleanKey = hadArraySuffix ? key.slice(0, -2) : key

      const isObject = value && typeof value === 'object' && !Array.isArray(value)

      if (isObject) {
        // recurse into nested objects
        acc[cleanKey] = prepareParamsForQSLibrary(value as Record<string, unknown>)
      } else {
        if (hadArraySuffix) {
          acc[cleanKey] = Array.isArray(value) ? value : [value]
        } else {
          acc[cleanKey] = value
        }
      }

      return acc
    },
    {} as Record<string, unknown>,
  )
}

export async function getCsv(setCsvExportingIsLoading: (state: boolean) => void, query: ExportDataParams) {
  if (!query) {
    emitToast({
      message: 'CSV export: Query is required',
    })

    return
  }

  setCsvExportingIsLoading(true)

  try {
    const preparedQuery = prepareParamsForQSLibrary(query as unknown as Record<string, unknown>)

    const queryString = qs.stringify(preparedQuery, {
      arrayFormat: 'brackets',
      encodeValuesOnly: true,
    })

    const url = `/admin/api/exports?${queryString}`

    const { _data: data } = await orvalFetch<{
      _data: Any
      response: Response
    }>(
      {
        url,
        method: 'GET',
        responseType: 'text',
      },
      {
        includeHeaders: true,
      },
    )

    downloadCsv(data)
  } catch (error) {
    console.error('Error csv export:', error)
  } finally {
    setCsvExportingIsLoading(false)
  }
}

function downloadCsv(data: string, fileName?: string) {
  if (typeof document === 'undefined') {
    return
  }

  const blob = new Blob([data], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName || `${dayjs().tz(getAppTimezone()).format('YYYY-MM-DD_HH-mm-ss')}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
